<template>
    <v-dialog :value="value" fullscreen hide-overlay persistent transition="dialog-bottom-transition" @input="$emit('input', $event)">
        <v-card class="content-root" tile>
            <v-app-bar flat color="transparent" class="content-appbar" height="72">
                <v-btn icon @click="close" :disabled="loading || saving || uploadingImage"><v-icon>mdi-close</v-icon></v-btn>

                <div class="ml-2 content-title-box">
                    <div class="mercury--text text-caption">{{ readonly ? 'Visualização do artigo' : 'Editor do artigo' }}</div>
                    <div class="stardust--text font-weight-medium one-line-text">
                        {{ loading ? 'Gerando conteúdo com IA...' : (readonly ? 'Somente leitura' : 'Refine antes de publicar') }}
                    </div>
                </div>

                <v-spacer />

                <v-btn v-if="!readonly" color="primary" large :loading="saving" :disabled="!canSave" @click="save">
                    <v-icon left>mdi-content-save-outline</v-icon>
                    Salvar
                </v-btn>

                <v-chip v-else small color="mercury" text-color="white">
                    <v-icon small left>mdi-eye-outline</v-icon>
                    Visualização
                </v-chip>
            </v-app-bar>

            <div v-if="!loading && !error" class="meta-strip">
                <div class="meta-field meta-field--title-row">
                    <label class="meta-label">Título do artigo</label>
                    <input v-model="articleTitle" type="text" class="meta-input meta-input--title" placeholder="Informe o título do artigo" :disabled="saving || readonly" />
                    <span class="meta-counter" :class="titleCounterClass">{{ articleTitle.length }} / 60</span>
                </div>

                <div class="meta-field meta-field--author">
                    <label class="meta-label">Autor</label>
                    <input v-model="articleAuthor" type="text" class="meta-input" placeholder="Nome do autor" :disabled="saving || readonly" />
                </div>

                <div class="meta-field meta-field--focus">
                    <label class="meta-label">Palavra-chave principal</label>
                    <input v-model="focusKeyword" type="text" class="meta-input" placeholder="ex: tratamento de varizes" :disabled="saving || readonly" />
                </div>

                <div class="meta-field meta-field--meta-desc">
                    <label class="meta-label">Meta descrição (SEO)</label>
                    <textarea v-model="metaDescription" rows="2" class="meta-input meta-input--textarea" placeholder="Resumo que aparece no Google logo abaixo do título (140-160 caracteres)" :disabled="saving || readonly"></textarea>
                    <span class="meta-counter" :class="metaCounterClass">{{ metaDescription.length }} / 160</span>
                </div>
            </div>

            <div class="content-body">
                <!-- LOADING -->
                <transition name="fade">
                    <div v-if="loading" class="state-loading">
                        <div class="loading-orb">
                            <v-icon size="40" color="primary">mdi-creation</v-icon>
                        </div>
                        <h2 class="text-h5 stardust--text font-weight-semibold mt-6 mb-2">Escrevendo seu artigo</h2>
                        <p class="mercury--text text-body-2 ma-0 mb-6" style="max-width: 500px; text-align: center;">
                            A IA está combinando o tema, os tópicos e a análise dos concorrentes para produzir um conteúdo otimizado para SEO.
                        </p>

                        <div v-if="streamItems.length" class="context-stream" aria-hidden="true">
                            <transition-group name="stream" tag="div" class="context-stream-inner">
                                <div v-for="item in visibleStream" :key="item.key" class="context-chip" :class="`context-chip--${item.kind}`">
                                    <div class="context-chip-icon">
                                        <v-icon small :color="item.color">{{ item.icon }}</v-icon>
                                    </div>
                                    <div class="context-chip-body">
                                        <div class="context-chip-kind">{{ item.kindLabel }}</div>
                                        <div class="context-chip-label">{{ item.label }}</div>
                                    </div>
                                </div>
                            </transition-group>
                        </div>

                        <div v-if="streamCounters.length" class="stream-counters">
                            <span v-for="c in streamCounters" :key="c.kind" class="stream-counter">
                                <v-icon small :color="c.color" class="mr-1">{{ c.icon }}</v-icon>
                                <strong>{{ c.count }}</strong>
                                <span class="ml-1">{{ c.label }}</span>
                            </span>
                        </div>

                        <p class="mercury--text text-caption ma-0 mt-8 mb-2 d-inline-flex align-center" style="opacity: 0.8;">
                            <v-icon small color="mercury" class="mr-1">mdi-clock-outline</v-icon>
                            A geração pode levar até 3 minutos. Não feche esta janela.
                        </p>
                        <div class="loading-bar">
                            <div class="loading-bar-fill"></div>
                        </div>
                    </div>
                </transition>

                <!-- ERROR -->
                <transition name="fade">
                    <div v-if="!loading && error" class="state-error">
                        <v-icon size="48" color="error">mdi-alert-circle-outline</v-icon>
                        <h2 class="text-h5 stardust--text font-weight-semibold mt-4 mb-2">Algo deu errado</h2>
                        <p class="mercury--text text-body-2 mb-6" style="max-width: 480px; text-align: center;">
                            {{ error }}
                        </p>
                        <v-btn color="primary" large @click="$emit('retry')">
                            <v-icon left>mdi-refresh</v-icon>
                            Tentar novamente
                        </v-btn>
                    </div>
                </transition>

                <!-- EDITOR -->
                <div v-show="!loading && !error && editor" class="editor-shell">
                    <div v-if="!readonly" class="editor-toolbar">
                        <v-btn-toggle dense multiple background-color="transparent" class="toolbar-group">
                            <v-btn small :value="isActive('bold')" :input-value="isActive('bold')" @click="run('toggleBold')" title="Negrito (Ctrl+B)">
                                <v-icon small>mdi-format-bold</v-icon>
                            </v-btn>
                            <v-btn small :value="isActive('italic')" :input-value="isActive('italic')" @click="run('toggleItalic')" title="Itálico (Ctrl+I)">
                                <v-icon small>mdi-format-italic</v-icon>
                            </v-btn>
                            <v-btn small :value="isActive('strike')" :input-value="isActive('strike')" @click="run('toggleStrike')" title="Tachado">
                                <v-icon small>mdi-format-strikethrough</v-icon>
                            </v-btn>
                        </v-btn-toggle>

                        <div class="toolbar-divider"></div>

                        <v-btn-toggle dense multiple background-color="transparent" class="toolbar-group">
                            <v-btn small :input-value="isHeading(1)" @click="toggleHeading(1)" title="Título 1">
                                <span class="heading-label">H1</span>
                            </v-btn>
                            <v-btn small :input-value="isHeading(2)" @click="toggleHeading(2)" title="Título 2">
                                <span class="heading-label">H2</span>
                            </v-btn>
                            <v-btn small :input-value="isHeading(3)" @click="toggleHeading(3)" title="Título 3">
                                <span class="heading-label">H3</span>
                            </v-btn>
                            <v-btn small :input-value="isActive('paragraph')" @click="run('setParagraph')" title="Parágrafo">
                                <v-icon small>mdi-format-paragraph</v-icon>
                            </v-btn>
                        </v-btn-toggle>

                        <div class="toolbar-divider"></div>

                        <v-btn-toggle dense multiple background-color="transparent" class="toolbar-group">
                            <v-btn small :input-value="isActive('bulletList')" @click="run('toggleBulletList')" title="Lista">
                                <v-icon small>mdi-format-list-bulleted</v-icon>
                            </v-btn>
                            <v-btn small :input-value="isActive('orderedList')" @click="run('toggleOrderedList')" title="Lista numerada">
                                <v-icon small>mdi-format-list-numbered</v-icon>
                            </v-btn>
                            <v-btn small :input-value="isActive('blockquote')" @click="run('toggleBlockquote')" title="Citação">
                                <v-icon small>mdi-format-quote-close</v-icon>
                            </v-btn>
                            <v-btn small :input-value="isActive('codeBlock')" @click="run('toggleCodeBlock')" title="Bloco de código">
                                <v-icon small>mdi-code-tags</v-icon>
                            </v-btn>
                        </v-btn-toggle>

                        <div class="toolbar-divider"></div>

                        <v-menu offset-y bottom>
                            <template #activator="{ on, attrs }">
                                <v-btn-toggle dense background-color="transparent" class="toolbar-group">
                                    <v-btn small :input-value="isActive('table')" v-on="on" v-bind="attrs" title="Tabela">
                                        <v-icon small>mdi-table</v-icon>
                                        <v-icon x-small>mdi-menu-down</v-icon>
                                    </v-btn>
                                </v-btn-toggle>
                            </template>
                            <v-list dense>
                                <v-list-item @click="insertTable">
                                    <v-list-item-icon class="mr-3"><v-icon small>mdi-table-plus</v-icon></v-list-item-icon>
                                    <v-list-item-title>Inserir tabela</v-list-item-title>
                                </v-list-item>
                                <v-divider />
                                <v-list-item :disabled="!isActive('table')" @click="run('addRowAfter')">
                                    <v-list-item-icon class="mr-3"><v-icon small>mdi-table-row-plus-after</v-icon></v-list-item-icon>
                                    <v-list-item-title>Adicionar linha abaixo</v-list-item-title>
                                </v-list-item>
                                <v-list-item :disabled="!isActive('table')" @click="run('addColumnAfter')">
                                    <v-list-item-icon class="mr-3"><v-icon small>mdi-table-column-plus-after</v-icon></v-list-item-icon>
                                    <v-list-item-title>Adicionar coluna à direita</v-list-item-title>
                                </v-list-item>
                                <v-list-item :disabled="!isActive('table')" @click="run('deleteRow')">
                                    <v-list-item-icon class="mr-3"><v-icon small>mdi-table-row-remove</v-icon></v-list-item-icon>
                                    <v-list-item-title>Remover linha</v-list-item-title>
                                </v-list-item>
                                <v-list-item :disabled="!isActive('table')" @click="run('deleteColumn')">
                                    <v-list-item-icon class="mr-3"><v-icon small>mdi-table-column-remove</v-icon></v-list-item-icon>
                                    <v-list-item-title>Remover coluna</v-list-item-title>
                                </v-list-item>
                                <v-divider />
                                <v-list-item :disabled="!isActive('table')" @click="run('deleteTable')">
                                    <v-list-item-icon class="mr-3"><v-icon small>mdi-table-remove</v-icon></v-list-item-icon>
                                    <v-list-item-title>Excluir tabela</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>

                        <div class="toolbar-group">
                            <v-btn
                                small
                                :loading="uploadingImage"
                                :disabled="saving || uploadingImage"
                                title="Inserir imagem do computador"
                                aria-label="Inserir imagem do computador"
                                @mousedown.prevent
                                @click="pickContentImage"
                            >
                                <v-icon small>mdi-image-plus-outline</v-icon>
                            </v-btn>
                        </div>

                        <v-btn-toggle v-if="isActive('image')" dense background-color="transparent" class="toolbar-group" aria-label="Largura da imagem">
                            <v-btn
                                v-for="width in [25, 50, 75, 100]"
                                :key="width"
                                small
                                :input-value="isActive('image', { width: width + '%' })"
                                :disabled="saving || uploadingImage"
                                :title="`Largura da imagem: ${width}%`"
                                :aria-label="`Largura da imagem: ${width}%`"
                                @mousedown.prevent
                                @click="setImageWidth(width)"
                            >
                                {{ width }}%
                            </v-btn>
                        </v-btn-toggle>

                        <v-btn-toggle v-if="isActive('image')" dense background-color="transparent" class="toolbar-group" aria-label="Alinhamento da imagem">
                            <v-btn
                                v-for="alignment in imageAlignments"
                                :key="alignment.value"
                                small
                                :input-value="isActive('image', { alignment: alignment.value })"
                                :disabled="saving || uploadingImage"
                                :title="alignment.label"
                                :aria-label="alignment.label"
                                @mousedown.prevent
                                @click="setImageAlignment(alignment.value)"
                            >
                                <v-icon small>{{ alignment.icon }}</v-icon>
                            </v-btn>
                        </v-btn-toggle>

                        <div class="toolbar-divider"></div>

                        <v-btn-toggle dense background-color="transparent" class="toolbar-group">
                            <v-btn small @click="run('undo')" title="Desfazer (Ctrl+Z)">
                                <v-icon small>mdi-undo</v-icon>
                            </v-btn>
                            <v-btn small @click="run('redo')" title="Refazer (Ctrl+Shift+Z)">
                                <v-icon small>mdi-redo</v-icon>
                            </v-btn>
                        </v-btn-toggle>

                        <v-spacer />

                        <button type="button" class="ai-toolbar-btn" :class="{ 'is-ready': hasSelection, 'is-pulsing': !hasSelection && !aiHintDismissed }" @click="onAiToolbarClick" title="Revisar trecho com IA">
                            <v-icon small color="white" class="mr-1">mdi-creation</v-icon>
                            <span>Revisar com IA</span>
                            <span v-if="hasSelection" class="ai-toolbar-btn-hint">(trecho selecionado)</span>
                        </button>
                    </div>

                    <transition name="ai-hint-fade">
                        <div v-if="!readonly && !aiHintDismissed && !hasSelection" class="ai-hint-banner">
                            <div class="ai-hint-icon">
                                <v-icon small color="primary">mdi-lightbulb-on-outline</v-icon>
                            </div>
                            <div class="ai-hint-body">
                                <strong>Novo:</strong>
                                selecione qualquer trecho do artigo e clique em
                                <span class="ai-hint-chip"><v-icon x-small color="white">mdi-creation</v-icon>Revisar com IA</span>
                                para melhorar o texto com instruções em linguagem natural.
                            </div>
                            <v-btn icon x-small @click="dismissAiHint" title="Entendi">
                                <v-icon small>mdi-close</v-icon>
                            </v-btn>
                        </div>
                    </transition>

                    <div class="editor-split">
                        <div class="editor-canvas">
                            <editor-content :editor="editor" class="editor-content-area" />
                        </div>

                        <div class="editor-seo-rail">
                            <ArtigosSeoPanel :html="currentHtml" :title="articleTitle" :meta-description="metaDescription" :focus-keyword="focusKeyword" />
                        </div>
                    </div>
                </div>

                <!-- AI REVISION BUBBLE -->
                <transition name="ai-bubble-fade">
                    <button v-if="!readonly && aiRevision.visible && !aiRevision.panelOpen && !aiRevision.diffOpen" type="button" class="ai-bubble" :style="{ left: aiRevision.bubbleX + 'px', top: aiRevision.bubbleY + 'px' }" @mousedown.prevent @click="openRevisionPanel">
                        <v-icon small color="white" class="mr-1">mdi-creation</v-icon>
                        <span>Revisar com IA</span>
                    </button>
                </transition>

                <!-- AI REVISION INSTRUCTIONS PANEL -->
                <transition name="ai-panel-fade">
                    <div v-if="aiRevision.panelOpen" class="ai-panel-backdrop" @click.self="aiRevision.loading ? null : cancelRevision()">
                        <div class="ai-panel-card" role="dialog" aria-modal="true">
                            <div class="ai-panel-header">
                                <v-icon color="primary" class="mr-2">mdi-creation</v-icon>
                                <div>
                                    <div class="stardust--text font-weight-semibold">Revisar com IA</div>
                                    <div class="mercury--text text-caption">Descreva como este trecho deve ser melhorado.</div>
                                </div>
                            </div>

                            <div class="ai-panel-snippet">
                                <div class="ai-panel-snippet-label">Trecho selecionado</div>
                                <div class="ai-panel-snippet-text">{{ aiRevision.originalText }}</div>
                            </div>

                            <v-textarea ref="aiInstructions" v-model="aiRevision.instructions" outlined dense hide-details rows="3" auto-grow placeholder="Ex: deixe mais direto e conciso, corrija o tom, inclua um exemplo..." :disabled="aiRevision.loading" class="ai-panel-textarea" @keydown.meta.enter="submitRevision" @keydown.ctrl.enter="submitRevision" />

                            <div v-if="aiRevision.error" class="ai-panel-error">
                                <v-icon small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
                                {{ aiRevision.error }}
                            </div>

                            <div class="ai-panel-actions">
                                <v-btn text :disabled="aiRevision.loading" @click="cancelRevision">Cancelar</v-btn>
                                <v-btn color="primary" :loading="aiRevision.loading" :disabled="!aiRevision.instructions.trim()" @click="submitRevision">
                                    <v-icon left small>mdi-creation</v-icon>
                                    Revisar
                                </v-btn>
                            </div>
                        </div>
                    </div>
                </transition>

                <!-- AI REVISION DIFF PREVIEW -->
                <transition name="ai-panel-fade">
                    <div v-if="aiRevision.diffOpen" class="ai-panel-backdrop" @click.self="rejectRevision">
                        <div class="ai-panel-card ai-diff-card" role="dialog" aria-modal="true">
                            <div class="ai-panel-header">
                                <v-icon color="primary" class="mr-2">mdi-creation</v-icon>
                                <div>
                                    <div class="stardust--text font-weight-semibold">Revisão sugerida</div>
                                    <div class="mercury--text text-caption">Aceite para substituir o trecho original.</div>
                                </div>
                            </div>

                            <div class="ai-diff-section ai-diff-original">
                                <div class="ai-diff-label">Original</div>
                                <div class="ai-diff-text">{{ aiRevision.originalText }}</div>
                            </div>

                            <div class="ai-diff-arrow">
                                <v-icon small color="primary">mdi-arrow-down</v-icon>
                            </div>

                            <div class="ai-diff-section ai-diff-revised">
                                <div class="ai-diff-label">Revisado</div>
                                <div class="ai-diff-text">{{ aiRevision.revisedText }}</div>
                            </div>

                            <div class="ai-panel-actions">
                                <v-btn text @click="rejectRevision">
                                    <v-icon left small>mdi-close</v-icon>
                                    Recusar
                                </v-btn>
                                <v-btn color="primary" @click="acceptRevision">
                                    <v-icon left small>mdi-check</v-icon>
                                    Aceitar
                                </v-btn>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>

            <v-dialog v-model="confirmCloseDialog" max-width="620" persistent>
                <v-card class="close-confirm-card rounded-lg">
                    <v-card-title class="close-confirm-title">
                        <span>Salvar alterações?</span>
                    </v-card-title>
                    <v-card-text class="close-confirm-text">
                        Você fez alterações neste artigo. Salve antes de fechar ou descarte as mudanças feitas nesta edição.
                    </v-card-text>
                    <v-card-actions class="close-confirm-actions">
                        <v-btn text :disabled="saving" @click="keepEditing">
                            Continuar editando
                        </v-btn>
                        <v-spacer />
                        <v-btn text color="error" :disabled="saving" @click="discardAndClose">
                            Descartar
                        </v-btn>
                        <v-btn color="primary" :loading="saving" :disabled="!canSave" @click="saveAndClose">
                            <v-icon left small>mdi-content-save-outline</v-icon>
                            Salvar e fechar
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card>
    </v-dialog>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableHeader from '@tiptap/extension-table-header'
import TableCell from '@tiptap/extension-table-cell'
import Image from '@tiptap/extension-image'
import { emitToastr } from '@/lib/Utils'
import Api from '@/lib/Api'
import ArtigosSeoPanel from '@/components/ArtigosSeoPanel.vue'
import { computeSeoScore } from '@/lib/seoScore'

export default {
    name: 'ArtigosContentDialog',
    components: { EditorContent, ArtigosSeoPanel },
    props: {
        value: { type: Boolean, default: false },
        html: { type: String, default: '' },
        loading: { type: Boolean, default: false },
        error: { type: String, default: '' },
        context: { type: Object, default: null },
        initialTitle: { type: String, default: '' },
        initialAuthor: { type: String, default: '' },
        initialMetaDescription: { type: String, default: '' },
        initialFocusKeyword: { type: String, default: '' },
        saving: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
    },
    data() {
        return {
            editor: null,
            currentHtml: '',
            uploadingImage: false,
            imageAlignments: [
                { value: 'left', icon: 'mdi-format-align-left', label: 'Alinhar imagem à esquerda' },
                { value: 'center', icon: 'mdi-format-align-center', label: 'Centralizar imagem' },
                { value: 'right', icon: 'mdi-format-align-right', label: 'Alinhar imagem à direita' },
            ],
            // tick counter forces toolbar to recompute is-active on selection change
            tick: 0,
            streamCursor: 0,
            streamTimer: null,
            articleTitle: '',
            articleAuthor: '',
            metaDescription: '',
            focusKeyword: '',
            confirmCloseDialog: false,
            initialSnapshot: null,
            aiRevision: {
                visible: false,
                bubbleX: 0,
                bubbleY: 0,
                panelOpen: false,
                instructions: '',
                loading: false,
                error: '',
                range: null,
                originalText: '',
                revisedText: '',
                diffOpen: false,
            },
            aiHintDismissed: false,
        }
    },
    computed: {
        hasContent() {
            return !!(this.currentHtml && this.currentHtml.replace(/<[^>]*>/g, '').trim())
        },
        canSave() {
            return !this.readonly && this.hasContent && !this.loading && !this.saving && !this.uploadingImage && !!this.articleTitle.trim()
        },
        hasUnsavedChanges() {
            if (!this.initialSnapshot) return false
            const current = this.getSnapshot()
            return Object.keys(current).some(key => current[key] !== this.initialSnapshot[key])
        },
        titleCounterClass() {
            const len = this.articleTitle.length
            if (len === 0) return 'meta-counter--neutral'
            if (len >= 50 && len <= 60) return 'meta-counter--good'
            if (len >= 40 && len <= 65) return 'meta-counter--ok'
            return 'meta-counter--bad'
        },
        metaCounterClass() {
            const len = this.metaDescription.length
            if (len === 0) return 'meta-counter--neutral'
            if (len >= 140 && len <= 160) return 'meta-counter--good'
            if (len >= 120 && len <= 170) return 'meta-counter--ok'
            return 'meta-counter--bad'
        },
        hasSelection() {
            // eslint-disable-next-line no-unused-expressions
            this.tick
            if (!this.editor) return false
            const { from, to, empty } = this.editor.state.selection
            return !empty && from !== to
        },
        streamItems() {
            const items = []
            const ctx = this.context || {}
            const seen = new Set()
            const push = (item) => {
                const dedupKey = `${item.kind}::${(item.label || '').toLowerCase().trim()}`
                if (seen.has(dedupKey)) return
                seen.add(dedupKey)
                items.push(item)
            }

            if (ctx.theme) {
                push({
                    kind: 'theme',
                    kindLabel: 'Tema principal',
                    label: ctx.theme,
                    icon: 'mdi-target',
                    color: 'primary',
                })
            }

            const actions = [
                'Mapeando intenção de busca',
                'Analisando estrutura dos artigos vencedores',
                'Definindo hierarquia de headings',
                'Selecionando palavras-chave semânticas',
                'Avaliando lacunas de conteúdo',
                'Ajustando tom de voz da marca',
                'Otimizando densidade de palavras-chave',
                'Planejando meta descrição',
                'Estruturando introdução envolvente',
                'Identificando perguntas-chave do público',
                'Gerando subtítulos atrativos',
                'Balanceando SEO e leitura natural',
            ]
            for (const a of actions) {
                push({
                    kind: 'action',
                    kindLabel: 'Passo da IA',
                    label: a,
                    icon: 'mdi-auto-fix',
                    color: 'primary',
                })
            }

            const topics = Array.isArray(ctx.topics) ? ctx.topics : []
            for (const t of topics) {
                if (!t || !t.label) continue
                const isPaa = t.source === 'paa'
                push({
                    kind: isPaa ? 'paa' : 'topic',
                    kindLabel: isPaa ? 'Pergunta selecionada' : 'Tópico selecionado',
                    label: t.label,
                    icon: isPaa ? 'mdi-help-circle-outline' : 'mdi-bullseye-arrow',
                    color: 'galaxy',
                })
            }

            const paa = Array.isArray(ctx.peopleAlsoAsk) ? ctx.peopleAlsoAsk : []
            for (const p of paa) {
                const label = (p && (p.question || p.label)) || ''
                if (!label) continue
                push({
                    kind: 'paa',
                    kindLabel: 'Pergunta frequente',
                    label,
                    icon: 'mdi-help-circle-outline',
                    color: 'galaxy',
                })
            }

            const allRelated = Array.isArray(ctx.allRelatedSearches) && ctx.allRelatedSearches.length
                ? ctx.allRelatedSearches
                : (Array.isArray(ctx.relatedSearches) ? ctx.relatedSearches : [])
            for (const r of allRelated) {
                const label = typeof r === 'string' ? r : (r && r.query) || ''
                if (!label) continue
                push({
                    kind: 'related',
                    kindLabel: 'Busca relacionada',
                    label,
                    icon: 'mdi-magnify',
                    color: 'mercury',
                })
            }

            const selected = Array.isArray(ctx.organic) ? ctx.organic : []
            const serp = Array.isArray(ctx.serpOrganic) ? ctx.serpOrganic : []
            const selectedLinks = new Set(selected.map(s => s && s.link).filter(Boolean))
            for (const o of selected) {
                if (!o || !o.title) continue
                push({
                    kind: 'competitor',
                    kindLabel: 'Concorrente analisado',
                    label: o.title,
                    icon: 'mdi-trophy-outline',
                    color: 'warning',
                })
                const domain = this.prettyDomain(o.link)
                if (domain) {
                    push({
                        kind: 'domain',
                        kindLabel: 'Domínio analisado',
                        label: domain,
                        icon: 'mdi-web',
                        color: 'warning',
                    })
                }
            }
            for (const s of serp) {
                if (!s || !s.title) continue
                if (s.link && selectedLinks.has(s.link)) continue
                push({
                    kind: 'serp',
                    kindLabel: `Resultado SERP${s.position ? ' #' + s.position : ''}`,
                    label: s.title,
                    icon: 'mdi-google',
                    color: 'mercury',
                })
            }

            return items.map((it, i) => ({ ...it, index: i }))
        },
        visibleStream() {
            const items = this.streamItems
            const n = items.length
            if (!n) return []
            const slots = Math.min(3, n)
            const out = []
            for (let i = 0; i < slots; i++) {
                const idx = (this.streamCursor + i) % n
                const item = items[idx]
                // Key rotates as items cycle so transition-group detects add/remove
                out.push({ ...item, key: `${item.index}-${Math.floor((this.streamCursor + i) / n)}` })
            }
            return out
        },
        streamCounters() {
            const buckets = {
                topic: { label: 'tópicos', icon: 'mdi-bullseye-arrow', color: 'galaxy', count: 0 },
                paa: { label: 'perguntas', icon: 'mdi-help-circle-outline', color: 'galaxy', count: 0 },
                related: { label: 'buscas', icon: 'mdi-magnify', color: 'mercury', count: 0 },
                competitor: { label: 'concorrentes', icon: 'mdi-trophy-outline', color: 'warning', count: 0 },
                serp: { label: 'páginas SERP', icon: 'mdi-google', color: 'mercury', count: 0 },
            }
            for (const it of this.streamItems) {
                if (buckets[it.kind]) buckets[it.kind].count += 1
            }
            return Object.entries(buckets)
                .filter(([, v]) => v.count > 0)
                .map(([kind, v]) => ({ kind, ...v }))
        },
    },
    watch: {
        value(open) {
            if (open) {
                this.articleTitle = this.initialTitle || ''
                this.articleAuthor = this.initialAuthor || ''
                this.metaDescription = this.initialMetaDescription || ''
                this.focusKeyword = this.initialFocusKeyword || ''
                this.confirmCloseDialog = false
                this.initialSnapshot = null
                this.$nextTick(() => this.ensureEditor())
            } else {
                this.confirmCloseDialog = false
                this.initialSnapshot = null
                this.destroyEditor()
                this.stopStream()
            }
        },
        html(newHtml) {
            if (!this.editor) return
            const current = this.editor.getHTML()
            if (current === newHtml) return
            this.editor.commands.setContent(newHtml || '', false)
            this.currentHtml = this.editor.getHTML()
        },
        initialTitle(v) {
            if (!this.articleTitle && v) this.articleTitle = v
        },
        initialAuthor(v) {
            if (!this.articleAuthor && v) this.articleAuthor = v
        },
        initialMetaDescription(v) {
            if (!this.metaDescription && v) this.metaDescription = v
        },
        initialFocusKeyword(v) {
            if (!this.focusKeyword && v) this.focusKeyword = v
        },
        loading: {
            immediate: true,
            handler(isLoading) {
                if (isLoading) {
                    this.startStream()
                } else {
                    this.stopStream()
                }
            },
        },
    },
    mounted() {
        try {
            this.aiHintDismissed = localStorage.getItem('artigos_ai_hint_dismissed') === '1'
        } catch (e) { /* noop */ }
    },
    beforeDestroy() {
        this.destroyEditor()
        this.stopStream()
    },
    methods: {
        ensureEditor() {
            if (this.editor) return
            this.editor = new Editor({
                content: this.html || '',
                editable: !this.readonly,
                extensions: [
                    StarterKit,
                    Table.configure({ resizable: false }),
                    TableRow,
                    TableHeader,
                    TableCell,
                    Image.extend({
                        addAttributes() {
                            return {
                                ...this.parent(),
                                width: {
                                    default: null,
                                    parseHTML: element => element.style.width || null,
                                    renderHTML: ({ width }) => width ? { style: `width: ${width};` } : {},
                                },
                                alignment: {
                                    default: 'center',
                                    parseHTML: element => {
                                        const alignment = element.getAttribute('align') || element.style.textAlign
                                        return ['left', 'center', 'right'].includes(alignment) ? alignment : 'center'
                                    },
                                    renderHTML: ({ alignment }) => ({
                                        align: alignment,
                                        style: `display: block; float: none; margin-left: ${alignment === 'left' ? '0' : 'auto'}; margin-right: ${alignment === 'right' ? '0' : 'auto'};`,
                                    }),
                                },
                            }
                        },
                    }).configure({ HTMLAttributes: { style: 'max-width: 100%; height: auto;' } }),
                ],
                onUpdate: () => {
                    this.currentHtml = this.editor.getHTML()
                },
                onSelectionUpdate: () => {
                    this.tick++
                    if (!this.readonly) this.updateAiBubble()
                },
                onTransaction: () => {
                    this.tick++
                },
            })
            this.currentHtml = this.editor.getHTML()
            this.captureInitialSnapshot()
        },
        destroyEditor() {
            if (this.editor) {
                const ed = this.editor
                this.editor = null
                try { ed.destroy() } catch (e) { /* noop */ }
            }
            this.currentHtml = ''
            this.aiRevision = {
                visible: false,
                bubbleX: 0,
                bubbleY: 0,
                panelOpen: false,
                instructions: '',
                loading: false,
                error: '',
                range: null,
                originalText: '',
                revisedText: '',
                diffOpen: false,
            }
        },
        // The unused `tick` reference below is intentional: it makes the
        // computed/method reactive to selection changes so the toolbar updates.
        isActive(name, attrs) {
            // eslint-disable-next-line no-unused-expressions
            this.tick
            return this.editor ? this.editor.isActive(name, attrs) : false
        },
        isHeading(level) {
            return this.isActive('heading', { level })
        },
        run(command) {
            if (!this.editor) return
            const chain = this.editor.chain().focus()
            if (typeof chain[command] !== 'function') return
            chain[command]().run()
        },
        toggleHeading(level) {
            if (!this.editor) return
            this.editor.chain().focus().toggleHeading({ level }).run()
        },
        insertTable() {
            if (!this.editor) return
            this.editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
        },
        setImageWidth(width) {
            if (this.readonly || this.loading || this.saving || this.uploadingImage || !this.isActive('image') || ![25, 50, 75, 100].includes(width)) return
            this.editor.chain().focus().updateAttributes('image', { width: `${width}%` }).run()
        },
        setImageAlignment(alignment) {
            if (this.readonly || this.loading || this.saving || this.uploadingImage || !this.isActive('image')) return
            if (!['left', 'center', 'right'].includes(alignment)) return
            this.editor.chain().focus().updateAttributes('image', { alignment }).run()
        },
        pickContentImage() {
            if (!this.editor || this.readonly || this.loading || this.saving || this.uploadingImage) return
            const editor = this.editor
            const position = editor.state.selection.to
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = 'image/jpeg,image/png,image/webp'
            input.onchange = (event) => {
                const file = event.target.files?.[0]
                if (file) this.uploadContentImage(file, editor, position)
            }
            input.click()
        },
        async uploadContentImage(file, editor, position) {
            if (!file || !editor || editor !== this.editor || editor.isDestroyed || this.readonly || this.saving || this.uploadingImage) return
            if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
                emitToastr('error', 'Selecione uma imagem JPG, PNG ou WebP.')
                return
            }
            // Keep the insertion point in place if the text changes during the upload.
            const mapPosition = ({ transaction }) => { position = transaction.mapping.map(position) }
            editor.on('transaction', mapPosition)
            this.uploadingImage = true
            try {
                const response = await Api.uploadImage(file)
                if (editor !== this.editor || editor.isDestroyed || !this.value || this.readonly) return
                if (response?.error || typeof response?.message !== 'string' || !/^https?:\/\//i.test(response.message)) {
                    emitToastr('error', response?.error && response.message || 'Erro ao enviar imagem. Tente novamente.')
                    return
                }
                editor.chain().focus(position).setImage({ src: response.message, alt: '' }).run()
            } catch (err) {
                if (editor === this.editor && !editor.isDestroyed && this.value) {
                    emitToastr('error', err?.message || 'Erro ao enviar imagem. Tente novamente.')
                }
            } finally {
                editor.off('transaction', mapPosition)
                this.uploadingImage = false
            }
        },
        close() {
            if (this.uploadingImage) return
            if (this.readonly || this.loading || this.error || !this.hasUnsavedChanges) {
                this.closeWithoutSaving()
                return
            }
            this.confirmCloseDialog = true
        },
        keepEditing() {
            this.confirmCloseDialog = false
        },
        discardAndClose() {
            this.confirmCloseDialog = false
            this.closeWithoutSaving()
        },
        closeWithoutSaving() {
            this.$emit('input', false)
        },
        saveAndClose() {
            if (!this.canSave) return
            this.save()
        },
        save() {
            if (!this.editor || this.uploadingImage) return
            let html = this.editor.getHTML()
            const renderedImages = this.editor.view.dom.querySelectorAll('img')
            if (renderedImages.length) {
                const content = document.createElement('div')
                content.innerHTML = html
                content.querySelectorAll('img').forEach((image, index) => {
                    const rendered = renderedImages[index]
                    if (!rendered?.naturalWidth || !rendered.naturalHeight) return
                    const width = Math.round(rendered.getBoundingClientRect().width)
                    if (width <= 0) return
                    // Send native dimensions too: the blog importer may discard inline CSS.
                    image.setAttribute('width', width)
                    image.setAttribute('height', Math.max(1, Math.round(width * rendered.naturalHeight / rendered.naturalWidth)))
                    image.style.setProperty('max-width', '100%')
                    image.style.setProperty('height', 'auto')
                    image.style.setProperty('object-fit', 'contain')
                })
                html = content.innerHTML
            }
            const title = (this.articleTitle || '').trim()
            const metaDescription = (this.metaDescription || '').trim()
            const focusKeyword = (this.focusKeyword || '').trim()
            const { score } = computeSeoScore({ html, title, metaDescription, focusKeyword })
            this.$emit('save', {
                html,
                title,
                author: (this.articleAuthor || '').trim(),
                metaDescription,
                focusKeyword,
                seoScore: score,
            })
        },
        getSnapshot() {
            return {
                html: this.normalizeSnapshotValue(this.currentHtml),
                title: this.normalizeSnapshotValue(this.articleTitle),
                author: this.normalizeSnapshotValue(this.articleAuthor),
                metaDescription: this.normalizeSnapshotValue(this.metaDescription),
                focusKeyword: this.normalizeSnapshotValue(this.focusKeyword),
            }
        },
        captureInitialSnapshot() {
            this.initialSnapshot = this.getSnapshot()
        },
        normalizeSnapshotValue(value) {
            return (value || '').trim()
        },
        prettyDomain(link) {
            if (!link || typeof link !== 'string') return ''
            try {
                return new URL(link).hostname.replace(/^www\./, '')
            } catch {
                return ''
            }
        },
        startStream() {
            this.stopStream()
            this.streamCursor = 0
            if (this.streamItems.length <= 3) return
            this.streamTimer = setInterval(() => {
                this.streamCursor = (this.streamCursor + 1) % this.streamItems.length
            }, 1800)
        },
        stopStream() {
            if (this.streamTimer) {
                clearInterval(this.streamTimer)
                this.streamTimer = null
            }
        },
        resetAiRevision() {
            this.aiRevision = {
                visible: false,
                bubbleX: 0,
                bubbleY: 0,
                panelOpen: false,
                instructions: '',
                loading: false,
                error: '',
                range: null,
                originalText: '',
                revisedText: '',
                diffOpen: false,
            }
        },
        updateAiBubble() {
            const ai = this.aiRevision
            if (ai.panelOpen || ai.diffOpen || ai.loading) return
            if (!this.editor) {
                ai.visible = false
                return
            }
            const { from, to, empty } = this.editor.state.selection
            if (empty || from === to) {
                ai.visible = false
                return
            }
            try {
                const start = this.editor.view.coordsAtPos(from)
                const end = this.editor.view.coordsAtPos(to)
                const midX = (Math.min(start.left, end.left) + Math.max(start.right || start.left, end.right || end.left)) / 2
                const top = Math.min(start.top, end.top)
                ai.bubbleX = midX
                ai.bubbleY = Math.max(top - 12, 8)
                ai.visible = true
            } catch {
                ai.visible = false
            }
        },
        openRevisionPanel() {
            if (!this.editor) return
            const { from, to, empty } = this.editor.state.selection
            if (empty) return
            const originalText = this.editor.state.doc.textBetween(from, to, ' ').trim()
            if (!originalText) return
            this.aiRevision.range = { from, to }
            this.aiRevision.originalText = originalText
            this.aiRevision.instructions = ''
            this.aiRevision.error = ''
            this.aiRevision.panelOpen = true
            this.aiRevision.visible = false
            this.$nextTick(() => {
                const el = this.$refs.aiInstructions
                if (el && typeof el.focus === 'function') el.focus()
            })
        },
        cancelRevision() {
            this.resetAiRevision()
            this.updateAiBubble()
        },
        async submitRevision() {
            const instructions = (this.aiRevision.instructions || '').trim()
            if (!instructions) {
                this.aiRevision.error = 'Descreva o que você quer revisar.'
                return
            }
            this.aiRevision.loading = true
            this.aiRevision.error = ''
            try {
                const resp = await Api.reviseTextWithAI(this.aiRevision.originalText, instructions)
                if (!resp || resp.error) {
                    this.aiRevision.error = (resp && resp.message) || 'Não foi possível revisar o trecho.'
                    return
                }
                const revised = resp.message && resp.message.revised_text
                if (!revised || typeof revised !== 'string') {
                    this.aiRevision.error = 'Resposta inválida da IA.'
                    return
                }
                this.aiRevision.revisedText = revised
                this.aiRevision.panelOpen = false
                this.aiRevision.diffOpen = true
            } catch (err) {
                this.aiRevision.error = (err && err.message) || 'Falha ao revisar.'
            } finally {
                this.aiRevision.loading = false
            }
        },
        acceptRevision() {
            if (!this.editor || !this.aiRevision.range || !this.aiRevision.revisedText) {
                this.resetAiRevision()
                return
            }
            const { from, to } = this.aiRevision.range
            const text = this.aiRevision.revisedText
            this.editor
                .chain()
                .focus()
                .insertContentAt({ from, to }, text)
                .run()
            this.resetAiRevision()
        },
        rejectRevision() {
            this.resetAiRevision()
        },
        onAiToolbarClick() {
            if (!this.editor) return
            if (!this.hasSelection) {
                emitToastr('info', 'Selecione um trecho do artigo para revisar com IA.')
                this.editor.chain().focus().run()
                return
            }
            this.openRevisionPanel()
        },
        dismissAiHint() {
            this.aiHintDismissed = true
            try { localStorage.setItem('artigos_ai_hint_dismissed', '1') } catch (e) { /* noop */ }
        },
    },
}
</script>

<style scoped>
.content-root {
    background-color: var(--color-background) !important;
    background-image:
        var(--bg-mesh-1),
        var(--bg-mesh-2),
        var(--bg-mesh-3),
        var(--bg-mesh-4),
        var(--bg-mesh-5),
        var(--bg-mesh-6) !important;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
}

.content-appbar {
    background: var(--glass-nav-bg) !important;
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--glass-nav-border) !important;
    position: sticky;
    top: 0;
    z-index: 4;
}

.content-title-box {
    min-width: 0;
    max-width: 520px;
}

.one-line-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.content-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
}

/* LOADING */
.state-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    flex: 1;
    padding: 30px 24px;
    min-height: 60vh;
}

.loading-orb {
    width: 96px;
    height: 96px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(112, 42, 225, 0.18) 0%, rgba(112, 42, 225, 0.08) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: var(--shadow-pulse);
    animation: orb-pulse 2.4s ease-in-out infinite;
}

@keyframes orb-pulse {

    0%,
    100% {
        transform: scale(1);
        box-shadow: var(--shadow-pulse);
    }

    50% {
        transform: scale(1.06);
        box-shadow: 0 0 30px rgba(112, 42, 225, 0.35);
    }
}

/* CONTEXT STREAM */
.context-stream {
    width: 100%;
    max-width: 520px;
    margin-top: 8px;
    position: relative;
    mask-image: linear-gradient(180deg, transparent 0%, #000 14%, #000 86%, transparent 100%);
    -webkit-mask-image: linear-gradient(180deg, transparent 0%, #000 14%, #000 86%, transparent 100%);
}

.context-stream-inner {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 4px;
    min-height: 210px;
}

.context-chip {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: var(--glass-panel-bg);
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
    border: 1px solid var(--glass-panel-border);
    border-radius: var(--radius-sm);
    box-shadow: var(--shadow-glass);
    min-height: 60px;
    transition: transform 520ms cubic-bezier(0.22, 1, 0.36, 1), opacity 520ms ease;
}

.context-chip-icon {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: var(--radius-sm);
    background: rgba(112, 42, 225, 0.10);
    display: flex;
    align-items: center;
    justify-content: center;
}

.context-chip--theme .context-chip-icon {
    background: linear-gradient(135deg, rgba(112, 42, 225, 0.18), rgba(112, 42, 225, 0.06));
}

.context-chip--topic .context-chip-icon,
.context-chip--paa .context-chip-icon {
    background: rgba(100, 72, 178, 0.14);
}

.context-chip--competitor .context-chip-icon {
    background: rgba(234, 179, 8, 0.14);
}

.context-chip--related .context-chip-icon,
.context-chip--serp .context-chip-icon {
    background: var(--glass-soft-bg);
}

.context-chip--action .context-chip-icon {
    background: linear-gradient(135deg, rgba(112, 42, 225, 0.20), rgba(112, 42, 225, 0.06));
}

.context-chip--domain .context-chip-icon {
    background: rgba(234, 179, 8, 0.10);
}

.context-chip-body {
    min-width: 0;
    flex: 1;
    text-align: left;
}

.context-chip-kind {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--color-on-surface-variant);
    margin-bottom: 2px;
}

.context-chip-label {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-on-surface);
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* stream transitions */
.stream-enter-active,
.stream-leave-active {
    transition: opacity 520ms ease, transform 520ms cubic-bezier(0.22, 1, 0.36, 1);
}

.stream-move {
    transition: transform 520ms cubic-bezier(0.22, 1, 0.36, 1);
}

.stream-enter {
    opacity: 0;
    transform: translateY(24px) scale(0.96);
}

.stream-leave-to {
    opacity: 0;
    transform: translateY(-24px) scale(0.96);
}

.stream-leave-active {
    position: absolute;
    left: 4px;
    right: 4px;
}

/* COUNTERS */
.stream-counters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 14px;
    margin-top: 18px;
    justify-content: center;
    max-width: 520px;
}

.stream-counter {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    color: var(--color-on-surface-variant);
    background: var(--glass-soft-bg);
    padding: 6px 10px;
    border-radius: var(--radius-pill);
    border: 1px solid var(--glass-panel-border);
}

.stream-counter strong {
    color: var(--color-on-surface);
    font-variant-numeric: tabular-nums;
    font-weight: 700;
}

.loading-bar {
    width: 240px;
    height: 4px;
    border-radius: var(--radius-pill);
    background: rgba(112, 42, 225, 0.14);
    overflow: hidden;
    position: relative;
}

.loading-bar-fill {
    position: absolute;
    inset: 0;
    width: 40%;
    background: linear-gradient(90deg, transparent, var(--color-primary), transparent);
    border-radius: var(--radius-pill);
    animation: bar-slide 1.6s ease-in-out infinite;
}

@keyframes bar-slide {
    0% {
        transform: translateX(-120%);
    }

    100% {
        transform: translateX(320%);
    }
}

/* ERROR */
.state-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 48px 24px;
    min-height: 60vh;
}

/* CLOSE CONFIRMATION */
.close-confirm-card {
    overflow: hidden;
}

.close-confirm-title {
    display: flex;
    align-items: center;
    gap: 12px;
    color: var(--color-on-surface);
    font-size: 18px;
    font-weight: 700;
    line-height: 1.35;
    padding-bottom: 8px;
}

.close-confirm-icon {
    width: 40px;
    height: 40px;
    border-radius: var(--radius-sm);
    background: rgba(245, 158, 11, 0.12);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.close-confirm-text {
    color: var(--color-on-surface-variant);
    font-size: 14px;
    line-height: 1.55;
}

.close-confirm-actions {
    gap: 8px;
    flex-wrap: wrap;
    padding: 12px 24px 20px;
}

@media (max-width: 560px) {
    .close-confirm-actions {
        align-items: stretch;
        flex-direction: column-reverse;
    }

    .close-confirm-actions .v-btn {
        margin-left: 0 !important;
        width: 100%;
    }

    .close-confirm-actions .spacer {
        display: none;
    }
}

/* META STRIP (title + author + focus_keyword + meta_description em 2 linhas) */
.meta-strip {
    background: var(--glass-soft-bg);
    -webkit-backdrop-filter: blur(8px);
    backdrop-filter: blur(8px);
    border-bottom: 1px solid var(--glass-panel-border);
    padding: 14px 24px;
    display: grid;
    grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr) minmax(0, 1.2fr);
    gap: 18px 20px;
    align-items: flex-end;
}

.meta-field {
    display: flex;
    flex-direction: column;
    min-width: 0;
    position: relative;
}

/* meta description sempre ocupa linha inteira (segunda linha) */
.meta-field--meta-desc {
    grid-column: 1 / -1;
}

@media (max-width: 900px) {
    .meta-strip {
        grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
    }

    .meta-field--focus {
        grid-column: 1 / -1;
    }
}

@media (max-width: 600px) {
    .meta-strip {
        grid-template-columns: 1fr;
    }
}

.meta-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--color-on-surface-variant);
    margin-bottom: 4px;
}

.meta-input {
    border: 1px solid var(--glass-panel-border);
    border-radius: var(--radius-sm);
    padding: 10px 14px;
    font-size: 14px;
    font-family: inherit;
    color: var(--color-on-surface);
    background: var(--color-surface-container-lowest);
    outline: none;
    transition: border-color 160ms ease, box-shadow 160ms ease;
    width: 100%;
}

.meta-input:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(112, 42, 225, 0.14);
}

.meta-input:disabled {
    background: var(--glass-soft-bg);
    color: var(--color-on-surface-variant);
    cursor: not-allowed;
}

html.theme-dark .meta-input {
    background: var(--color-surface-container);
}

html.theme-dark .meta-input:disabled {
    background: var(--glass-soft-bg);
}

.meta-input--title {
    font-size: 17px;
    font-weight: 600;
}

.meta-input--textarea {
    resize: vertical;
    min-height: 56px;
    line-height: 1.4;
}

.meta-counter {
    position: absolute;
    right: 6px;
    bottom: -16px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    padding: 1px 6px;
    border-radius: 999px;
    line-height: 1.4;
}

.meta-counter--neutral {
    color: var(--color-on-surface-variant);
    background: transparent;
}

.meta-counter--good {
    color: var(--color-success);
    background: var(--color-success-soft);
}

.meta-counter--ok {
    color: #b45309;
    background: rgba(245, 158, 11, 0.14);
}

html.theme-dark .meta-counter--ok {
    color: #fbbf24;
}

.meta-counter--bad {
    color: var(--color-error);
    background: rgba(180, 19, 64, 0.10);
}

/* EDITOR */
.editor-shell {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.editor-split {
    flex: 1;
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 0 16px;
    min-height: 0;
}

.editor-split>.editor-canvas {
    flex: 1;
    min-width: 0;
    padding: 32px 0 80px;
    overflow-y: auto;
}

.editor-seo-rail {
    position: sticky;
    top: 132px;
    padding-top: 24px;
    padding-bottom: 24px;
    flex-shrink: 0;
}

@media (max-width: 960px) {
    .editor-split {
        flex-direction: column;
        padding: 0;
    }

    .editor-seo-rail {
        position: static;
        width: 100%;
        padding: 0 16px 24px;
    }

    .editor-seo-rail>>>.seo-panel {
        width: 100%;
    }
}

.editor-toolbar {
    background: var(--glass-nav-bg);
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--glass-nav-border);
    padding: 8px 16px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    position: sticky;
    top: 72px;
    z-index: 3;
}

.toolbar-group {
    background: transparent !important;
    border-radius: var(--radius-xs);
    box-shadow: none !important;
}

.toolbar-group>>>.v-btn {
    background: transparent !important;
    border: none !important;
    min-width: 36px !important;
    height: 36px !important;
    padding: 0 8px !important;
    border-radius: var(--radius-xs) !important;
    color: var(--color-on-surface-variant);
}

.toolbar-group>>>.v-btn:hover {
    background: rgba(112, 42, 225, 0.08) !important;
    color: var(--color-on-surface);
}

.toolbar-group>>>.v-btn--active {
    background: rgba(112, 42, 225, 0.14) !important;
    color: var(--color-primary) !important;
}

.toolbar-divider {
    width: 1px;
    height: 24px;
    background: var(--glass-panel-border);
    margin: 0 4px;
}

.heading-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
}

.editor-canvas {
    flex: 1;
    padding: 32px 16px 80px;
    overflow-y: auto;
}

.editor-content-area {
    max-width: 820px;
    margin: 0 auto;
    background: var(--color-surface-container-lowest);
    border: 1px solid var(--glass-panel-border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-glass);
    min-height: 60vh;
}

html.theme-dark .editor-content-area {
    background: var(--color-surface-container);
}

/* TipTap content typography (deep selectors since editor renders unscoped) */
.editor-content-area>>>.ProseMirror {
    padding: 56px 64px;
    outline: none;
    color: var(--color-on-surface);
    font-size: 17px;
    line-height: 1.75;
    min-height: 60vh;
}

.editor-content-area>>>.ProseMirror:focus {
    outline: none;
}

.editor-content-area>>>.ProseMirror img {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 24px auto;
}

.editor-content-area>>>.ProseMirror img.ProseMirror-selectednode {
    outline: 2px solid var(--color-primary);
    outline-offset: 4px;
}

.editor-content-area>>>.ProseMirror h1 {
    font-family: var(--font-display);
    font-size: 2.1rem;
    font-weight: 800;
    line-height: 1.25;
    margin: 1.6em 0 0.6em;
    color: var(--color-on-surface);
}

.editor-content-area>>>.ProseMirror h2 {
    font-family: var(--font-display);
    font-size: 1.55rem;
    font-weight: 700;
    line-height: 1.3;
    margin: 1.6em 0 0.5em;
    color: var(--color-on-surface);
}

.editor-content-area>>>.ProseMirror h3 {
    font-family: var(--font-display);
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.35;
    margin: 1.4em 0 0.4em;
    color: var(--color-on-surface);
}

.editor-content-area>>>.ProseMirror h1:first-child,
.editor-content-area>>>.ProseMirror h2:first-child,
.editor-content-area>>>.ProseMirror h3:first-child {
    margin-top: 0;
}

.editor-content-area>>>.ProseMirror p {
    margin: 0 0 1.05em;
}

.editor-content-area>>>.ProseMirror ul,
.editor-content-area>>>.ProseMirror ol {
    margin: 0 0 1.05em;
    padding-left: 1.4em;
}

.editor-content-area>>>.ProseMirror li {
    margin-bottom: 0.4em;
}

.editor-content-area>>>.ProseMirror li>p {
    margin: 0;
}

.editor-content-area>>>.ProseMirror blockquote {
    border-left: 3px solid var(--color-primary);
    padding: 0.4em 1em;
    margin: 1.2em 0;
    color: var(--color-on-surface-variant);
    font-style: italic;
    background: rgba(112, 42, 225, 0.06);
    border-radius: 0 var(--radius-xs) var(--radius-xs) 0;
}

.editor-content-area>>>.ProseMirror code {
    background: var(--glass-soft-bg);
    padding: 0.1em 0.35em;
    border-radius: 4px;
    font-size: 0.92em;
    color: var(--color-on-surface);
}

.editor-content-area>>>.ProseMirror pre {
    background: var(--color-dark-violet);
    color: var(--color-on-primary);
    padding: 1em 1.2em;
    border-radius: var(--radius-sm);
    overflow-x: auto;
    margin: 1.2em 0;
}

.editor-content-area>>>.ProseMirror pre code {
    background: transparent;
    color: inherit;
    padding: 0;
}

.editor-content-area>>>.ProseMirror a {
    color: var(--color-primary);
    text-decoration: underline;
    text-underline-offset: 2px;
}

.editor-content-area>>>.ProseMirror .tableWrapper {
    overflow-x: auto;
    margin: 1.2em 0;
}

.editor-content-area>>>.ProseMirror table {
    border-collapse: collapse;
    width: 100%;
    table-layout: fixed;
}

.editor-content-area>>>.ProseMirror th,
.editor-content-area>>>.ProseMirror td {
    border: 1px solid var(--glass-panel-border);
    padding: 8px 12px;
    vertical-align: top;
    position: relative;
}

.editor-content-area>>>.ProseMirror th {
    background: var(--glass-soft-bg);
    font-weight: 700;
    text-align: left;
}

.editor-content-area>>>.ProseMirror th>p,
.editor-content-area>>>.ProseMirror td>p {
    margin: 0;
}

.editor-content-area>>>.ProseMirror .selectedCell::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(112, 42, 225, 0.08);
    pointer-events: none;
}

/* TRANSITIONS */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 240ms ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

/* AI TOOLBAR BUTTON */
.ai-toolbar-btn {
    display: inline-flex;
    align-items: center;
    height: 32px;
    padding: 0 14px;
    border-radius: var(--radius-pill);
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dim) 100%);
    color: var(--color-on-primary);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
    border: none;
    cursor: pointer;
    font-family: inherit;
    box-shadow: 0 4px 14px rgba(112, 42, 225, 0.28), var(--shadow-soft);
    transition: transform 160ms ease, box-shadow 200ms ease, filter 200ms ease;
    position: relative;
}

.ai-toolbar-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(112, 42, 225, 0.38), var(--shadow-soft);
    filter: brightness(1.03);
}

.ai-toolbar-btn.is-ready {
    box-shadow: 0 0 0 3px rgba(112, 42, 225, 0.22), 0 8px 20px rgba(112, 42, 225, 0.38);
    animation: ai-ready-pop 320ms cubic-bezier(0.22, 1, 0.36, 1);
}

.ai-toolbar-btn.is-pulsing::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: var(--radius-pill);
    border: 2px solid rgba(112, 42, 225, 0.55);
    opacity: 0;
    animation: ai-btn-pulse 2.2s ease-in-out infinite;
    pointer-events: none;
}

.ai-toolbar-btn-hint {
    margin-left: 8px;
    font-weight: 500;
    opacity: 0.85;
    font-size: 11px;
}

@keyframes ai-ready-pop {
    0% {
        transform: scale(0.96);
    }

    60% {
        transform: scale(1.04);
    }

    100% {
        transform: scale(1);
    }
}

@keyframes ai-btn-pulse {
    0% {
        opacity: 0.8;
        transform: scale(1);
    }

    70% {
        opacity: 0;
        transform: scale(1.22);
    }

    100% {
        opacity: 0;
        transform: scale(1.22);
    }
}

/* AI HINT BANNER */
.ai-hint-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 12px auto 0;
    max-width: 820px;
    padding: 10px 14px;
    border-radius: var(--radius-sm);
    background: linear-gradient(135deg, rgba(112, 42, 225, 0.08) 0%, rgba(112, 42, 225, 0.04) 100%);
    border: 1px solid rgba(112, 42, 225, 0.22);
    color: var(--color-on-surface);
    font-size: 13px;
    line-height: 1.5;
    position: relative;
}

.ai-hint-banner strong {
    color: var(--color-primary);
    font-weight: 700;
}

.ai-hint-icon {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: var(--radius-xs);
    background: rgba(112, 42, 225, 0.14);
    display: flex;
    align-items: center;
    justify-content: center;
}

.ai-hint-body {
    flex: 1;
    min-width: 0;
}

.ai-hint-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: var(--radius-pill);
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dim) 100%);
    color: var(--color-on-primary);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
    vertical-align: middle;
    margin: 0 2px;
}

.ai-hint-chip .v-icon {
    margin-right: 3px;
}

.ai-hint-fade-enter-active,
.ai-hint-fade-leave-active {
    transition: opacity 220ms ease, transform 220ms ease, max-height 220ms ease;
    overflow: hidden;
}

.ai-hint-fade-enter,
.ai-hint-fade-leave-to {
    opacity: 0;
    transform: translateY(-4px);
    max-height: 0;
}

/* AI REVISION */
.ai-bubble {
    position: fixed;
    z-index: 20;
    transform: translate(-50%, -100%);
    display: inline-flex;
    align-items: center;
    padding: 8px 14px;
    border-radius: var(--radius-pill);
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dim) 100%);
    color: var(--color-on-primary);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.01em;
    border: none;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(112, 42, 225, 0.32), var(--shadow-soft);
    white-space: nowrap;
    font-family: inherit;
    transition: transform 160ms ease, box-shadow 200ms ease;
}

.ai-bubble:hover {
    transform: translate(-50%, -100%) translateY(-1px);
    box-shadow: 0 12px 28px rgba(112, 42, 225, 0.42), var(--shadow-soft);
}

.ai-bubble-fade-enter-active,
.ai-bubble-fade-leave-active {
    transition: opacity 160ms ease, transform 180ms ease;
}

.ai-bubble-fade-enter,
.ai-bubble-fade-leave-to {
    opacity: 0;
    transform: translate(-50%, -100%) translateY(6px);
}

.ai-panel-backdrop {
    position: fixed;
    inset: 0;
    z-index: 25;
    background: rgba(45, 0, 102, 0.45);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
}

.ai-panel-card {
    width: 100%;
    max-width: 560px;
    background: var(--color-surface-container-lowest);
    border: 1px solid var(--glass-panel-border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-glass-hover);
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-height: calc(100vh - 48px);
    overflow-y: auto;
}

html.theme-dark .ai-panel-card {
    background: var(--color-surface-container);
}

.ai-diff-card {
    max-width: 640px;
}

.ai-panel-header {
    display: flex;
    align-items: flex-start;
}

.ai-panel-snippet {
    background: var(--glass-soft-bg);
    border: 1px solid var(--glass-panel-border);
    border-radius: var(--radius-sm);
    padding: 12px 14px;
    max-height: 140px;
    overflow-y: auto;
}

.ai-panel-snippet-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--color-on-surface-variant);
    margin-bottom: 4px;
}

.ai-panel-snippet-text {
    font-size: 13px;
    line-height: 1.55;
    color: var(--color-on-surface);
    white-space: pre-wrap;
    word-break: break-word;
}

.ai-panel-textarea {
    font-size: 14px;
}

.ai-panel-error {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: var(--color-error);
    background: rgba(180, 19, 64, 0.08);
    border-radius: var(--radius-xs);
    padding: 8px 12px;
}

.ai-panel-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 4px;
}

.ai-panel-fade-enter-active,
.ai-panel-fade-leave-active {
    transition: opacity 200ms ease;
}

.ai-panel-fade-enter-active .ai-panel-card,
.ai-panel-fade-leave-active .ai-panel-card {
    transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease;
}

.ai-panel-fade-enter,
.ai-panel-fade-leave-to {
    opacity: 0;
}

.ai-panel-fade-enter .ai-panel-card,
.ai-panel-fade-leave-to .ai-panel-card {
    transform: translateY(16px) scale(0.98);
    opacity: 0;
}

.ai-diff-section {
    border-radius: 12px;
    padding: 14px 16px;
    border: 1px solid transparent;
    max-height: 220px;
    overflow-y: auto;
}

.ai-diff-original {
    background: var(--glass-soft-bg);
    border-color: var(--glass-panel-border);
}

.ai-diff-revised {
    background: rgba(112, 42, 225, 0.08);
    border-color: rgba(112, 42, 225, 0.28);
}

.ai-diff-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--color-on-surface-variant);
    margin-bottom: 6px;
}

.ai-diff-revised .ai-diff-label {
    color: var(--color-primary);
}

.ai-diff-text {
    font-size: 14px;
    line-height: 1.6;
    color: var(--color-on-surface);
    white-space: pre-wrap;
    word-break: break-word;
}

.ai-diff-original .ai-diff-text {
    color: var(--color-on-surface-variant);
}

.ai-diff-arrow {
    display: flex;
    justify-content: center;
    padding: 2px 0;
}

@media (max-width: 720px) {
    .editor-content-area>>>.ProseMirror {
        padding: 32px 24px;
        font-size: 16px;
    }

    .editor-canvas {
        padding: 16px 8px 64px;
    }
}
</style>
